import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { WifiOff } from 'lucide-react';
import { Button, Input } from '@mlm/ui';
import { useAuthStore } from '../../lib/auth-store';
import { OFFLINE_TOKEN } from '../../lib/offline/state';
import { offlineUsers, unlockWithPin } from '../../lib/offline/store';
import type { OfflineUser } from '../../lib/offline/types';

/**
 * The sign-in screen when the till has no internet. Only cashiers who signed in
 * here with internet and set a PIN can unlock it. This guards the device; it
 * gives no access to the server, so syncing still takes a real sign-in.
 */
export function OfflineUnlock() {
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const [users, setUsers] = useState<OfflineUser[] | null>(null);
  const [userId, setUserId] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void offlineUsers().then((rows) => {
      setUsers(rows);
      if (rows.length === 1) setUserId(rows[0]!.userId);
    });
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = await unlockWithPin(userId, pin);
      if (result.ok) {
        setSession(OFFLINE_TOKEN, result.profile);
        navigate('/');
      } else if (result.reason === 'locked') {
        setError('Too many wrong tries. Wait 5 minutes and try again.');
      } else {
        setError('Wrong PIN.');
      }
    } finally {
      setPin('');
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-rail p-4 text-rail-foreground">
      <form onSubmit={submit} className="w-full max-w-sm space-y-4">
        <div className="flex items-center gap-3">
          <WifiOff className="h-6 w-6" />
          <h1 className="text-xl font-semibold">No internet</h1>
        </div>
        {users && users.length === 0 ? (
          <p className="text-sm opacity-80">
            No one has set a till PIN on this device yet. Sign in once with internet to set one.
          </p>
        ) : (
          <>
            <p className="text-sm opacity-80">Unlock with your till PIN to keep selling.</p>
            <select
              aria-label="Cashier"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              required
              className="h-12 w-full rounded-md border border-rail-foreground/30 bg-transparent px-3 text-base"
            >
              <option value="" className="text-foreground">
                Choose your name
              </option>
              {(users ?? []).map((u) => (
                <option key={u.userId} value={u.userId} className="text-foreground">
                  {u.profile.fullName ?? u.profile.email}
                </option>
              ))}
            </select>
            <Input
              aria-label="Till PIN"
              type="password"
              inputMode="numeric"
              autoComplete="off"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              placeholder="Till PIN"
              className="h-12 border-rail-foreground/30 bg-transparent text-base text-rail-foreground"
            />
            {error && (
              <p role="alert" className="text-sm font-medium">
                {error}
              </p>
            )}
            <Button
              type="submit"
              className="h-12 w-full text-base"
              disabled={busy || !userId || !pin}
            >
              Unlock
            </Button>
          </>
        )}
      </form>
    </div>
  );
}
